/**
 * Advanced fuzzy search and ranking engine.
 * Ranks by pinned, exact/starts/contains/fuzzy relevance, recency and usage.
 */

export const fuzzyScore = (haystack, needle) => {
  if (!needle) return 0;
  haystack = String(haystack || '').toLowerCase();
  needle = String(needle || '').toLowerCase();
  if (haystack === needle) return 100;
  if (haystack.startsWith(needle)) return 80;
  if (haystack.includes(needle)) return 50;

  let score = 0;
  let haystackIdx = 0;
  let needleIdx = 0;
  while (needleIdx < needle.length && haystackIdx < haystack.length) {
    if (needle[needleIdx] === haystack[haystackIdx]) {
      score += 10;
      needleIdx++;
    }
    haystackIdx++;
  }
  return needleIdx === needle.length ? score : 0;
};

const templateRegex = /\{\{\s*[a-zA-Z0-9_]+\s*\}\}/;

export const filterBySection = (snippets, section, usageCounts = {}) => {
  const active = snippets || [];
  switch (section) {
    case 'pinned':
      return active.filter(s => s.isPinned);
    case 'text':
      return active.filter(s => !s.type || s.type === 'plain' || s.type === 'text');
    case 'templates':
    case 'variables':
      return active.filter(s => s.type === 'template' || templateRegex.test(s.content || ''));
    case 'html':
      return active.filter(s => s.type === 'html');
    case 'permanent':
      return active.filter(s => s.isPermanent);
    case 'unused':
      return active
        .filter(s => Math.max(Number(usageCounts[s.id] || 0), Number(s.usageCount || 0)) === 0)
        .sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0));
    case 'popular':
      return [...active].sort((a, b) => {
        const aUses = Math.max(Number(usageCounts[a.id] || 0), Number(a.usageCount || 0));
        const bUses = Math.max(Number(usageCounts[b.id] || 0), Number(b.usageCount || 0));
        return bUses - aUses || Number(b.lastUsedAt || 0) - Number(a.lastUsedAt || 0);
      });
    case 'recent':
      return [...active].sort((a, b) => {
        const aTime = Math.max(a.lastUsedAt || 0, a.updatedAt || 0, a.createdAt || 0);
        const bTime = Math.max(b.lastUsedAt || 0, b.updatedAt || 0, b.createdAt || 0);
        return bTime - aTime;
      });
    default:
      return active;
  }
};

const parseQuery = (query) => {
  const tokens = String(query || '').trim().split(/\s+/).filter(Boolean);
  const filters = {};
  const text = [];

  for (const token of tokens) {
    const match = token.match(/^([a-z]+):(.+)$/i);
    if (!match) {
      text.push(token);
      continue;
    }
    const [, rawKey, rawValue] = match;
    const key = rawKey.toLowerCase();
    const value = rawValue.toLowerCase();
    if (['type', 'is', 'has', 'used'].includes(key)) filters[key] = value;
    else text.push(token);
  }
  return { filters, text: text.join(' ') };
};

const matchesQueryFilters = (snippet, filters, usageCounts) => {
  if (filters.type) {
    const type = snippet.type || 'plain';
    if (filters.type === 'text' && !['plain', 'text'].includes(type)) return false;
    else if (filters.type !== 'text' && type !== filters.type) return false;
  }
  if (filters.is === 'pinned' && !snippet.isPinned) return false;
  if (filters.is === 'permanent' && !snippet.isPermanent) return false;
  if (filters.is === 'temporary' && snippet.isPermanent) return false;
  if (filters.has === 'variables' && !templateRegex.test(snippet.content || '')) return false;
  if (filters.used === 'never' && Math.max(Number(usageCounts[snippet.id] || 0), Number(snippet.usageCount || 0)) > 0) return false;
  return true;
};

export const rankSnippets = (snippets, query, pinnedIds = [], usageCounts = {}) => {
  const parsed = parseQuery(query);
  const filteredByOperators = (snippets || []).filter(s => matchesQueryFilters(s, parsed.filters, usageCounts));

  if (!parsed.text) {
    return filteredByOperators.map(s => ({
      ...s,
      score: pinnedIds.includes(s.id) ? 10000 : 0
    })).sort((a, b) =>
      b.score - a.score ||
      Number(b.lastUsedAt || b.updatedAt || 0) - Number(a.lastUsedAt || a.updatedAt || 0)
    );
  }

  const q = parsed.text.toLowerCase().trim();
  const now = Date.now();

  return filteredByOperators
    .map(s => {
      let score = 0;
      const title = String(s.title || '').toLowerCase();
      const content = String(s.content || '').toLowerCase();

      if (pinnedIds.includes(s.id)) score += 1000;

      if (title === q) score += 700;
      else if (title.startsWith(q)) score += 500;
      else if (title.includes(q)) score += 350;
      else score += fuzzyScore(title, q) * 2.5;

      if (content.includes(q)) score += 150;
      else score += fuzzyScore(content, q);

      const daysOld = (now - Number(s.lastUsedAt || s.createdAt || now)) / 86400000;
      if (daysOld < 7) score += Math.max(0, 120 - daysOld * 17);

      const count = Math.max(Number(usageCounts[s.id] || 0), Number(s.usageCount || 0));
      score += Math.min(80, count * 5);

      return { ...s, score };
    })
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score || String(b.title || '').localeCompare(String(a.title || '')));
};

export const extractVariables = (content) => {
  if (!content) return [];
  const regex = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  const matches = [...content.matchAll(regex)];
  return [...new Set(matches.map(m => m[1].trim()))];
};

export const applyVariables = (content, valuesMap) => {
  let result = content;
  Object.entries(valuesMap || {}).forEach(([key, val]) => {
    const escapedKey = key.replace(/[.*+?^$()|[\]\\{}]/g, '\\$&');
    const regex = new RegExp('\\{\\{\\s*' + escapedKey + '\\s*\\}\\}', 'g');
    result = result.replace(regex, val);
  });
  return result;
};

const normalizeForDuplicate = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const findExactDuplicateGroups = (snippets = []) => {
  const groups = new Map();
  for (const snippet of snippets.filter(s => !s.isDeleted)) {
    const normalized = normalizeForDuplicate(snippet.content);
    if (!normalized) continue;
    if (!groups.has(normalized)) groups.set(normalized, []);
    groups.get(normalized).push(snippet);
  }
  return [...groups.values()]
    .filter(group => group.length > 1)
    .sort((a, b) => b.length - a.length);
};

export const searchHelp = [
  'type:html',
  'type:text',
  'is:pinned',
  'is:permanent',
  'is:temporary',
  'has:variables',
  'used:never'
];